import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// Styled Components
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: #ffffffff;
  padding: 40px 24px;
`;

const FormWrapper = styled.form`
  background: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  padding: 48px 40px;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 420px;
  animation: ${fadeIn} 0.35s ease;
`;

const Logo = styled.h1`
  color: #fff;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 800;
  margin: 0 0 8px;
  letter-spacing: -0.5px;
  span { color: #fe88f1; }
`;

const Tagline = styled.p`
  color: rgba(255, 255, 255, 0.3);
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  text-align: center;
  margin: 0 0 32px;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.4);
  margin-bottom: 7px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1.5px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #fff;
  font-size: 0.95rem;
  font-family: inherit;
  box-sizing: border-box;
  margin-bottom: 18px;
  transition: border-color 0.2s;
  &:focus { outline: none; border-color: rgba(254, 136, 241, 0.5); }
  &::placeholder { color: rgba(255, 255, 255, 0.2); }
`;

const Button = styled.button`
  width: 100%;
  padding: 13px;
  background: #fe88f1;
  color: #0d0d0d;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.15s;
  &:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ErrorMsg = styled.p`
  color: #ff7e7e;
  font-size: 0.85rem;
  text-align: center;
  margin: 12px 0 0;
`;

const SuccessBanner = styled.div`
  background: rgba(0, 210, 100, 0.12);
  border: 1px solid rgba(0, 210, 100, 0.3);
  border-radius: 10px;
  padding: 20px;
  text-align: center;
  animation: ${fadeIn} 0.3s ease;
`;

const SuccessIcon = styled.div`
  font-size: 36px;
  margin-bottom: 10px;
`;

const SuccessText = styled.p`
  color: #4dde99;
  font-size: 0.92rem;
  font-weight: 600;
  margin: 0 0 6px;
`;

const SuccessSub = styled.p`
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.82rem;
  margin: 0;
  line-height: 1.5;
`;

const SignInLink = styled.p`
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.85rem;
  text-align: center;
  margin-top: 20px;
  a { color: #fe88f1; text-decoration: none; &:hover { text-decoration: underline; } }
`;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

const SignUpForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/signup`, {
        name: formData.name,
        email: formData.email,
        password: formData.password
      });
      setSuccess(true);
    } catch (err) {
      const errMsg = err.response?.data?.error || err.message;
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Helmet>
        <title>Create an Account — MyCinemaDB</title>
        <meta name="description" content="Join MyCinemaDB to discover movies, get AI-powered film recommendations, and connect with a community of cinephiles." />
      </Helmet>

      <FormWrapper onSubmit={handleSubmit}>
        <Logo>My<span>Cinema</span>DB</Logo>
        <Tagline>Create your account</Tagline>

        {success ? (
          <SuccessBanner>
            <SuccessIcon>📬</SuccessIcon>
            <SuccessText>Check your inbox!</SuccessText>
            <SuccessSub>
              We sent a verification email to <strong style={{ color: '#fff' }}>{formData.email}</strong>.
              Click the link inside to activate your account.
            </SuccessSub>
          </SuccessBanner>
        ) : (
          <>
            <FieldLabel htmlFor="su-name">Full Name</FieldLabel>
            <Input
              id="su-name"
              type="text"
              name="name"
              placeholder="Jane Doe"
              value={formData.name}
              onChange={handleChange}
              required
              autoComplete="name"
            />

            <FieldLabel htmlFor="su-email">Email Address</FieldLabel>
            <Input
              id="su-email"
              type="email"
              name="email"
              placeholder="you@email.com"
              value={formData.email}
              onChange={handleChange}
              required
              autoComplete="email"
            />

            <FieldLabel htmlFor="su-password">Password</FieldLabel>
            <Input
              id="su-password"
              type="password"
              name="password"
              placeholder="Min. 8 chars + number or symbol"
              value={formData.password}
              onChange={handleChange}
              required
              autoComplete="new-password"
            />

            <FieldLabel htmlFor="su-confirm">Confirm Password</FieldLabel>
            <Input
              id="su-confirm"
              type="password"
              name="confirmPassword"
              placeholder="Repeat your password"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              autoComplete="new-password"
            />

            <Button type="submit" disabled={loading}>
              {loading ? 'Creating account…' : 'Create Account'}
            </Button>

            {error && <ErrorMsg>{error}</ErrorMsg>}
          </>
        )}

        <SignInLink>
          Already have an account? <Link to="/sign-in">Sign In</Link>
        </SignInLink>
      </FormWrapper>
    </Container>
  );
};

export default SignUpForm;